import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  storeApp,
  storeuserRole,
} from "../../Store/TemplateStore/template.action";
import { API_URLS } from "../../network/appUrls";
import {
  getAccessRequest,
  updateRequests,
  saveUser,
  exportExcel,
} from "../../network/cipAPI";
import RequestStatusFilterHeader from "./customFilter";
import {
  faHome,
  faAngleLeft,
  faAngleRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AgTable from "../../components/AgTable";
import DupontIconButton from "../../components/DupontIconButton";
import DupontButton from "../../components/DupontButton";
import { OverlayLoader } from "../../components/PageLoading";
import { getFormattedDate } from "../../utils/Utils";
import moment from "moment";
import "./index.scss";

const AdminPage = () => {
  const basePath = API_URLS.basePath;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userName = useSelector((state) => state.userName);

  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("asc");
  const [exportLoading, setExportLoading] = useState(false);
  const userEmail = useSelector((state) => state.userEmail);
  const companyName = useSelector((state) => state.companyName);
  const country = useSelector((state) => state.country);

  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      // Export type set to 'request_access'
      const type = "request_access";
      const response = await exportExcel(type);
      if (response && response.data) {
        // Use backend filename if provided, else fallback
        let fileName = t("adminPage.accessRequestsFile");
        if (response.headers && response.headers["content-disposition"]) {
          const match = response.headers["content-disposition"].match(
            /filename="?([^";]+)"?/
          );
          if (match && match[1]) fileName = match[1];
        }
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Export Excel error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const formatToLocalDateTime = (dateString) => {
    if (!dateString) return "";

    try {
      const localMoment = moment.utc(dateString).local();

      if (!localMoment.isValid()) {
        return getFormattedDate(dateString);
      }

      return localMoment.format("DD/MM/YYYY, HH:mm:ss");
    } catch (error) {
      console.error("Error formatting date to local time:", error);
      return getFormattedDate(dateString);
    }
  };

  const payload = {
    email: userEmail,
    company: companyName,
    country: country,
    role: null,
    industry: null,
  };

  const saveUserDetails = async (payload) => {
    try {
      let response = await saveUser(payload);
      const { user_role } = response;
      dispatch(storeuserRole(`${user_role}`));
    } catch (error) {
      console.error("Error saving user information:", error);
    }
  };

  const onHomeClicked = () => {
    dispatch(storeApp(null));
    saveUserDetails(payload);
    const path = `${basePath}plant`;
    navigate(path);
  };

  const fetchAccessRequests = async (
    filters = currentFilters,
    sortfield = sortField,
    sortorder = sortOrder
  ) => {
    setShowOverLayLoader(true);
    try {
      const response = await getAccessRequest(
        currentPage,
        pageSize,
        filters,
        sortfield,
        sortorder
      );
      if (response && response.data) {
        setRowData(response.data.users || []);
        setTotalPages(response.data.total_pages || 1);
      } else {
        setRowData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching access requests:", error);
      setRowData([]);
      setTotalPages(1);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  useEffect(() => {
    fetchAccessRequests(currentFilters, sortField, sortOrder);
  }, [currentPage, pageSize, sortField, sortOrder]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const onSortChanged = (event) => {
    const sortModel = event.columnApi
      .getAllColumns()
      .map((column) => {
        return {
          colId: column.getColId(),
          sort: column.getSort(),
        };
      })
      .filter((col) => col.sort);

    if (sortModel && sortModel.length > 0) {
      const { colId, sort } = sortModel[0];
      setSortField(colId);
      setSortOrder(sort);
      fetchAccessRequests(currentFilters, colId, sort);
    } else {
      setSortField("created_at");
      setSortOrder("asc");
      fetchAccessRequests(currentFilters, "created_at", "asc");
    }
  };

  const StatusButtonsCellRenderer = (props) => {
    const { request_status, email } = props.data;
    const handleButtonClick = async (requestStatus) => {
      const payload = {
        user_email: email,
        request_status: requestStatus,
        comments: props.data.comments,
      };
      setShowOverLayLoader(true);
      try {
        await updateRequests(payload);
        fetchAccessRequests([]);
      } catch (error) {
        console.error("API error:", error);
      } finally {
        setShowOverLayLoader(false);
      }
    };
    return (
      <div className="status-buttons">
        {(request_status === 1 || request_status === 4) && (
          <>
            <DupontButton onClick={() => handleButtonClick(2)}>
              {t("adminPage.accept")}
            </DupontButton>
            <DupontButton
              customClass="small-red-btn"
              onClick={() => handleButtonClick(3)}
            >
              {t("adminPage.reject")}
            </DupontButton>
          </>
        )}
        {(request_status === 2 || request_status === 3) && (
          <DupontButton
            customClass="small-yellow-btn"
            onClick={() => handleButtonClick(4)}
          >
            {t("adminPage.revoke")}
          </DupontButton>
        )}
      </div>
    );
  };

  const onRequestStatusFilterChange = (filterQuery) => {
    const filters = filterQuery ? filterQuery.split(",") : [];
    setCurrentFilters(filters);
    fetchAccessRequests(filters);
  };

  const getStatusText = (status) => {
    switch (status) {
      case 1:
        return t("adminPage.request_submitted");
      case 2:
        return t("adminPage.accepted");
      case 3:
        return t("adminPage.rejected");
      case 4:
        return t("adminPage.revoked");
      default:
        return "Unknown Status";
    }
  };

  const colDefs = [
    {
      headerName: t("adminPage.userid"),
      field: "id",
      minWidth: 100,
      sortable: false,
    },
    {
      headerName: t("adminPage.userName"),
      field: "name",
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t("adminPage.company"),
      field: "company",
      minWidth: 200,
      sortable: false,
    },
    {
      headerName: t("adminPage.country"),
      field: "country",
      minWidth: 150,
      sortable: false,
    },
    {
      headerName: t("adminPage.email"),
      field: "email",
      minWidth: 250,
      sortable: false,
    },
    {
      headerName: t("adminPage.created_at"),
      field: "created_at",
      minWidth: 200,
      sortable: true,
      valueFormatter: (params) => {
        return formatToLocalDateTime(params.value);
      },
      sort: "desc",
    },
    {
      headerName: t("adminPage.request_status"),
      field: "request_status",
      minWidth: 250,
      sortable: false,
      headerComponent: RequestStatusFilterHeader,
      headerComponentParams: {
        onFilterChange: onRequestStatusFilterChange,
      },
      valueGetter: (params) => getStatusText(params.data.request_status),
    },
    {
      headerName: t("adminPage.comments"),
      field: "comments",
      minWidth: 150,
      sortable: false,
      cellEditorPopup: true,
      editable: true,
      cellEditor: "agLargeTextCellEditor",
      cellEditorParams: {
        maxLength: 100,
      },
      cellStyle: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "block",
      },
      tooltipValueGetter: (params) => {
        return params.value;
      },
    },
    {
      headerName: t("adminPage.actions"),
      field: "actions",
      minWidth: 300,
      cellRenderer: StatusButtonsCellRenderer,
      sortable: false,
    },
  ];

  const onCellValueChanged = async (event) => {
    const { data } = event;
    const payload = {
      user_email: data.email,
      request_status: data.request_status,
      comments: data.comments,
    };
    setShowOverLayLoader(true);
    try {
      await updateRequests(payload);
      fetchAccessRequests([]);
    } catch (error) {
      console.error("API error:", error);
    } finally {
      setShowOverLayLoader(false);
    }
  };

  return (
    <div className="container-main">
      {showOverLayLoader && <OverlayLoader customHeight={600} />}
      <div className="header_container">
        <div className="home flex-between-center">
          <div>
            <FontAwesomeIcon icon={faHome} onClick={onHomeClicked} />
            <span className="welcome-container">{`${t(
              "welcomeString.welcome"
            )}, ${userName}`}</span>
          </div>
        </div>
        <div className="header-divider" />
        <div className="container-dupont-icon">
          <div className="dupont-icon">
            <div>
              <img
                src={require("../../assets/DuPont_Logo.png")}
                height="30px"
              />
              <div className="water-solution">
                {t("headerString.waterSolution")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="main-container">
        <div className="admin-title">
          <span>{t("adminPage.manageAcess")}</span>
          <DupontButton onClick={handleExportExcel} disabled={exportLoading}>
            {exportLoading
              ? t("adminPage.exporting")
              : t("adminPage.exportExcel")}
          </DupontButton>
        </div>

        <div>
          <AgTable
            columnDefs={colDefs}
            rowData={rowData}
            customHeight="55vh"
            rowHeight={50}
            onSortChanged={onSortChanged}
            onCellValueChanged={onCellValueChanged}
            hideExport
          />
          <div className="pagination-controls">
            <label htmlFor="pageSize" className="page-size-label">
              {t("adminPage.pageSize")}
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="page-size-select"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <DupontIconButton
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn prev-btn"
              tooltip={t("adminPage.previous")}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </DupontIconButton>
            <span className="pagination-info">
              {t("adminPage.pageInfo", {
                current: currentPage,
                total: totalPages,
              })}
            </span>
            <DupontIconButton
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || totalPages === 0}
              className="pagination-btn next-btn"
              tooltip={t("adminPage.next")}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </DupontIconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
